import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Amount, StatCard, CustomSelect, MonthlyBarChart } from '@redpanda/ui';
import { toIntlLocale, useRefetchOnFocus } from '@redpanda/utils';
import { useDashboardData, yearOptions } from '../stores/reportStore';
import { useDataChanged } from '../utils/dataEvents';

const currentYear = new Date().getFullYear();

/**
 * 儀表板（D8 原則：只留每天要看、需要行動的數字，一列最多 4 張）
 * 4 張卡：本年收入、本年費用、本年淨利、應收帳款；下方每月收入與費用長條圖、右側銀行存款＋待處理清單（同 Xero、QuickBooks 首頁）
 * 預設今年；今年沒有已過帳分錄時提示最近有資料的年度（§七 預設期間）
 */
const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // 年度不記住（§R50：日期區間不記住），每次從今年開始
  const [year, setYear] = useState(currentYear);
  const { data, refetch } = useDashboardData({ year });

  useEffect(() => {
    refetch();
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(refetch);
  useDataChanged(['journals', 'invoices', 'bankTransactions', 'periods'], refetch);

  const locale = toIntlLocale(i18n.language);
  const money = (value: number) => `$${Math.round(value).toLocaleString(locale)}`;
  const compact = (value: number) =>
    `$${new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)}`;
  const monthLabel = (month: string) => new Date(`${month}-01T00:00:00`).toLocaleDateString(locale, { month: 'short' });

  const hasYearData = !!data && (data.income !== 0 || data.expense !== 0);
  const suggestYear = data && !hasYearData && data.latestPostedYear && data.latestPostedYear !== year ? data.latestPostedYear : null;

  // 銀行合計：兩個以上帳戶、而且幣別相同才加總（不同幣別不能直接相加）
  const bankTotal = data && data.bankAccounts.length > 1 && new Set(data.bankAccounts.map(a => a.currency)).size === 1
    ? data.bankAccounts.reduce((sum, a) => sum + a.balance, 0)
    : null;

  // 待處理：只列有數字的項目；全部清空時顯示一句話
  const todos = data ? [
    !data.hasCurrentFiscalYear && { key: 'fiscalYear', label: t('dashboard.todo.noFiscalYear', { year: currentYear }), to: '/period-management' },
    data.draftJournals > 0 && { key: 'drafts', label: t('dashboard.todo.draftJournals'), count: data.draftJournals, to: '/journals' },
    data.overdue.count > 0 && {
      key: 'overdue', label: t('dashboard.todo.overdueInvoices'), count: data.overdue.count, amount: data.overdue.amount, to: '/invoices', danger: true,
    },
    data.unreconciledTransactions > 0 && {
      key: 'reconcile', label: t('dashboard.todo.unreconciled'), count: data.unreconciledTransactions, to: '/bank-reconciliation',
    },
  ].filter(Boolean) as Array<{ key: string; label: string; count?: number; amount?: number; to: string; danger?: boolean }> : [];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('dashboard.title')}</h1>
          <div className="w-28">
            <CustomSelect
              value={String(year)}
              onChange={value => setYear(Number(value))}
              options={yearOptions().map(y => ({ value: String(y), label: t('dashboard.yearLabel', { year: y }) }))}
            />
          </div>
        </div>
      </div>

      {suggestYear && (
        <p className="mb-4 text-sm text-fg-secondary">
          {t('dashboard.noDataThisYear', { year })}
          <button type="button" onClick={() => setYear(suggestYear)} className="ml-2 font-medium text-accent hover:underline">
            {t('dashboard.viewYear', { year: suggestYear })}
          </button>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title={t('dashboard.stats.income', { year })} value={data ? <Amount value={data.income} /> : '—'} />
        <StatCard title={t('dashboard.stats.expense', { year })} value={data ? <Amount value={data.expense} /> : '—'} />
        <StatCard title={t('dashboard.stats.netIncome', { year })} value={data ? <Amount value={data.netIncome} /> : '—'} />
        <Link to="/invoices" className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <StatCard
            title={t('dashboard.stats.receivable')}
            value={data ? <Amount value={data.receivable.amount} /> : '—'}
            hint={data ? t('dashboard.stats.receivableHint', { count: data.receivable.count }) : undefined}
          />
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="text-base font-semibold text-fg mb-3">{t('dashboard.monthlyIncomeExpense')}</h2>
          <div className="bg-surface rounded-lg border border-line p-5">
            {data && hasYearData ? (
              <MonthlyBarChart
                data={data.monthly.map(m => ({ month: m.month, value: m.income, secondary: m.expense }))}
                seriesLabels={{ primary: t('dashboard.series.income'), secondary: t('dashboard.series.expense') }}
                formatValue={money}
                formatAxis={compact}
                formatMonth={monthLabel}
                height={240}
              />
            ) : (
              <p className="py-16 text-center text-sm text-fg-secondary">{t('dashboard.noPostedEntries', { year })}</p>
            )}
          </div>
        </section>

        <div className="space-y-6">
        <section>
          <h2 className="text-base font-semibold text-fg mb-3">{t('dashboard.bank.title')}</h2>
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            {!data ? (
              <p className="px-5 py-6 text-sm text-fg-secondary">—</p>
            ) : data.bankAccounts.length === 0 ? (
              <Link to="/bank-accounts" className="block px-5 py-6 text-sm text-fg-secondary hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors">
                {t('dashboard.bank.empty')}<span className="ml-2 font-medium text-accent">{t('dashboard.bank.add')} →</span>
              </Link>
            ) : (
              <ul className="divide-y divide-line">
                {data.bankAccounts.map(account => (
                  <li key={account.id}>
                    <Link to={`/bank-accounts/${account.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors">
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm text-fg">{account.accountName}</span>
                        <span className="block truncate text-[13px] text-fg-secondary">{account.bankName} ··{account.accountNumberLast4}</span>
                      </span>
                      <span className="text-sm font-medium text-fg"><Amount value={account.balance} /></span>
                    </Link>
                  </li>
                ))}
                {bankTotal !== null && (
                  <li className="flex items-center gap-3 px-5 py-3">
                    <span className="flex-1 text-sm text-fg-secondary">{t('dashboard.bank.total')}</span>
                    <span className="text-sm font-semibold text-fg"><Amount value={bankTotal} /></span>
                  </li>
                )}
              </ul>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold text-fg mb-3">{t('dashboard.todo.title')}</h2>
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            {todos.length === 0 ? (
              <p className="px-5 py-6 text-sm text-fg-secondary">{data ? t('dashboard.todo.allClear') : '—'}</p>
            ) : (
              <ul className="divide-y divide-line">
                {todos.map(item => (
                  <li key={item.key}>
                    <Link to={item.to} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors">
                      <span className="flex-1 min-w-0 text-sm text-fg">{item.label}</span>
                      {item.amount !== undefined && (
                        <span className="text-[13px] text-fg-secondary"><Amount value={item.amount} /></span>
                      )}
                      {item.count !== undefined && (
                        <span className={`text-sm font-medium tabular-nums ${item.danger ? 'text-red-600 dark:text-red-400' : 'text-fg'}`}>
                          {item.count}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
